import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SESSION_STORAGE_KEY = 'lv1_cta_dismissed';

const BuildTogetherPopup = ({ triggerRef }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Check if already dismissed this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  // IntersectionObserver to detect when trigger element is in view
  useEffect(() => {
    if (!triggerRef?.current || isDismissed) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          if (!isDismissed) {
            setIsVisible(false);
          }
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(triggerRef.current);
    return () => observer.disconnect();
  }, [triggerRef, isDismissed]);

  // Handle dismiss (clicking outside or explicit close)
  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
  };

  // Scroll to contact section
  const scrollToContact = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (isDismissed) return null;

  return (
    <>
      {/* Slim bottom bar - feels like a page extension */}
      <div
        className="fixed left-0 right-0 z-40"
        style={{
          bottom: 0,
          transform: isVisible
            ? 'translateY(0)'
            : 'translateY(100%)',
          transition: prefersReducedMotion
            ? 'transform 0.3s ease'
            : 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Subtle top edge glow */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 20%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.15) 80%, transparent 100%)',
          }}
        />

        {/* Main bar */}
        <div
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(to top, rgba(10,10,10,0.98) 0%, rgba(15,15,15,0.95) 100%)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {/* Subtle smoky atmosphere - very faint */}
          {!prefersReducedMotion && (
            <div
              className="absolute inset-0 overflow-hidden pointer-events-none"
              style={{ opacity: 0.15 }}
            >
              <div
                className="absolute inset-0"
                style={{
                  filter: 'blur(30px)',
                  animation: 'barSmoke 30s ease-in-out infinite',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '30%',
                    left: '-10%',
                    width: '120%',
                    height: '40px',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 30%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.4) 70%, transparent 100%)',
                    transform: 'rotate(-2deg)',
                    borderRadius: '30px',
                  }}
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div className="relative z-10 max-w-[1320px] mx-auto px-6">
            <div
              className="flex items-center justify-between gap-4 py-4"
              style={{
                paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
              }}
            >
              {/* Left: Text */}
              <div className="flex items-center gap-4 min-w-0">
                <p className="text-white/90 text-[14px] md:text-[15px] font-medium truncate">
                  Let's build something together
                </p>
                <span className="hidden sm:block text-white/40 text-[13px]">
                  —
                </span>
                <p className="hidden sm:block text-white/50 text-[13px] truncate">
                  Ready to explore a partnership?
                </p>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={scrollToContact}
                  className="px-5 py-2 bg-white text-[#0a0a0a] rounded-full text-[13px] font-medium hover:bg-white/90 transition-all duration-200"
                >
                  Get in Touch
                </button>
                <Link
                  to="/brands"
                  className="hidden md:inline-flex px-5 py-2 border border-white/20 text-white/80 rounded-full text-[13px] font-medium hover:bg-white/5 hover:border-white/30 hover:text-white transition-all duration-200"
                >
                  Our Brands
                </Link>
                <button
                  onClick={handleDismiss}
                  className="p-1.5 text-white/30 hover:text-white/60 transition-colors duration-200"
                  aria-label="Dismiss"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Keyframe for subtle smoke drift */}
      <style>{`
        @keyframes barSmoke {
          0%, 100% {
            transform: translateX(0);
            opacity: 0.6;
          }
          50% {
            transform: translateX(80px);
            opacity: 0.8;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-bar-smoke {
            animation: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default BuildTogetherPopup;
